import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../store/authStore';
import { Header } from '../components/Header';
import { SkipLink } from '../components/SkipLink';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import {
  Shield, Users, QrCode, Link2, LogIn, Settings2, Search,
  ChevronLeft, ChevronRight, Filter, X, Clock, Download, FileText,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_META = {
  auth:        { icon: LogIn,    color: 'bg-violet-50 text-violet-600', label: 'Auth' },
  team:        { icon: Users,    color: 'bg-blue-50 text-blue-600',     label: 'Team' },
  role:        { icon: Shield,   color: 'bg-amber-50 text-amber-600',   label: 'Role' },
  qr:          { icon: QrCode,   color: 'bg-emerald-50 text-emerald-600', label: 'QR Code' },
  integration: { icon: Link2,    color: 'bg-cyan-50 text-cyan-600',     label: 'Integration' },
  submission:  { icon: Settings2, color: 'bg-rose-50 text-rose-600',    label: 'Submission' },
};

const fallbackMeta = { icon: Clock, color: 'bg-zinc-50 text-zinc-600', label: 'Other' };

function AuditEntry({ entry, index }) {
  const meta = CATEGORY_META[entry.category] || fallbackMeta;
  const Icon = meta.icon;
  const ts = new Date(entry.timestamp);

  return (
    <motion.div
      initial={{ opacity: 0, x: -8 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.03 }}
      className="flex gap-4 py-3"
      data-testid={`audit-entry-${index}`}
    >
      {/* Icon */}
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${meta.color}`}>
        <Icon size={16} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className="text-sm text-[hsl(var(--foreground))]">
          <span className="font-semibold">{entry.user_name}</span>{' '}
          <span className="text-[hsl(var(--muted-foreground))]">{entry.detail || `${entry.action} (${entry.category})`}</span>
        </p>
        <div className="flex items-center gap-2 mt-0.5">
          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${meta.color}`}>{meta.label}</span>
          <span className="text-[10px] text-[hsl(var(--muted-foreground))]">{entry.action}</span>
          <span className="text-[10px] text-[hsl(var(--muted-foreground))]">
            {ts.toLocaleDateString()} {ts.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </span>
        </div>
      </div>
    </motion.div>
  );
}

export default function AuditLog() {
  const { token } = useAuth();
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [categories, setCategories] = useState([]);
  const [liveCount, setLiveCount] = useState(0);

  const fetchLog = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 30 };
      if (categoryFilter) params.category = categoryFilter;
      if (search.trim()) params.search = search.trim();

      const res = await axios.get(`${API}/audit-log`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setEntries(res.data.entries);
      setTotal(res.data.total);
      setTotalPages(res.data.total_pages);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('You do not have permission to view the audit log');
      } else {
        toast.error('Failed to load audit log');
      }
    } finally {
      setLoading(false);
    }
  }, [token, page, categoryFilter, search]);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/audit-log/categories`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCategories(res.data);
    } catch {}
  }, [token]);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);
  useEffect(() => { fetchLog(); }, [fetchLog]);

  // WebSocket for live audit events
  useEffect(() => {
    if (!token) return;
    const wsBase = API.replace(/^http/, 'ws').replace('/api', '');
    const ws = new WebSocket(`${wsBase}/api/ws/notifications/${token}`);

    ws.onmessage = (evt) => {
      try {
        const msg = JSON.parse(evt.data);
        if (msg.type === 'audit_event' && msg.entry) {
          // Only prepend if on page 1 and no active filters
          if (page === 1 && !categoryFilter && !search.trim()) {
            setEntries(prev => [msg.entry, ...prev].slice(0, 30));
            setTotal(prev => prev + 1);
          }
          setLiveCount(prev => prev + 1);
        }
      } catch {}
    };

    // Keep alive
    const ping = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) ws.send('ping');
    }, 25000);

    return () => { clearInterval(ping); ws.close(); };
  }, [token, page, categoryFilter, search]);

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="audit-log-page">
      <SkipLink />
      <Header />

      <main id="main-content" className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
            <div>
              <h1 className="text-3xl sm:text-4xl font-black mb-1" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="audit-title">
                Activity Audit Log
              </h1>
              <p className="text-[hsl(var(--muted-foreground))] flex items-center gap-2">
                {total} event{total !== 1 ? 's' : ''} recorded
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 text-xs font-medium" data-testid="live-indicator">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                  Live{liveCount > 0 ? ` (+${liveCount})` : ''}
                </span>
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="rounded-full" data-testid="export-csv-btn"
                onClick={async () => {
                  try {
                    const params = {};
                    if (categoryFilter) params.category = categoryFilter;
                    if (search.trim()) params.search = search.trim();
                    const res = await axios.get(`${API}/audit-log/export/csv`, {
                      headers: { Authorization: `Bearer ${token}` }, params, responseType: 'blob',
                    });
                    const url = window.URL.createObjectURL(new Blob([res.data]));
                    const a = document.createElement('a'); a.href = url; a.download = 'audit-log.csv';
                    document.body.appendChild(a); a.click(); a.remove();
                    toast.success('CSV downloaded');
                  } catch { toast.error('Export failed'); }
                }}>
                <Download className="h-4 w-4 mr-1" /> CSV
              </Button>
              <Button variant="outline" size="sm" className="rounded-full" data-testid="export-pdf-btn"
                onClick={async () => {
                  try {
                    const params = {};
                    if (categoryFilter) params.category = categoryFilter;
                    if (search.trim()) params.search = search.trim();
                    const res = await axios.get(`${API}/audit-log/export/pdf`, {
                      headers: { Authorization: `Bearer ${token}` }, params, responseType: 'blob',
                    });
                    const url = window.URL.createObjectURL(new Blob([res.data]));
                    const a = document.createElement('a'); a.href = url; a.download = 'audit-log.pdf';
                    document.body.appendChild(a); a.click(); a.remove();
                    toast.success('PDF downloaded');
                  } catch { toast.error('Export failed'); }
                }}>
                <FileText className="h-4 w-4 mr-1" /> PDF
              </Button>
            </div>
          </div>

          {/* Filters */}
          <Card className="p-4 mb-6 border border-[hsl(var(--border))]" data-testid="audit-filters">
            <div className="flex flex-col sm:flex-row gap-3">
              {/* Search */}
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[hsl(var(--muted-foreground))]" />
                <Input
                  value={search}
                  onChange={e => { setSearch(e.target.value); setPage(1); }}
                  placeholder="Search actions..."
                  className="pl-9"
                  data-testid="audit-search"
                />
              </div>

              {/* Category filter */}
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-[hsl(var(--muted-foreground))] shrink-0" />
                <select
                  value={categoryFilter}
                  onChange={e => { setCategoryFilter(e.target.value); setPage(1); }}
                  className="text-sm border border-[hsl(var(--border))] rounded-lg px-2 py-2 bg-white focus:ring-2 focus:ring-[#1d4ed8]"
                  data-testid="audit-category-filter"
                >
                  <option value="">All categories</option>
                  {categories.map(c => (
                    <option key={c} value={c}>{(CATEGORY_META[c] || fallbackMeta).label}</option>
                  ))}
                </select>
                {(categoryFilter || search) && (
                  <button onClick={() => { setCategoryFilter(''); setSearch(''); setPage(1); }}
                    className="p-1.5 hover:bg-[hsl(var(--muted))] rounded-lg" data-testid="audit-clear-filters">
                    <X className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />
                  </button>
                )}
              </div>
            </div>
          </Card>

          {/* Timeline */}
          {loading ? (
            <div className="text-center py-16" role="status">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#1d4ed8] mx-auto" />
              <p className="mt-3 text-sm text-[hsl(var(--muted-foreground))]">Loading...</p>
            </div>
          ) : entries.length === 0 ? (
            <Card className="p-12 text-center border border-[hsl(var(--border))]" data-testid="audit-empty">
              <Clock className="h-10 w-10 text-[hsl(var(--muted-foreground))] mx-auto mb-3" />
              <p className="font-semibold mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>No activity yet</p>
              <p className="text-sm text-[hsl(var(--muted-foreground))]">Actions will appear here as team members use the platform</p>
            </Card>
          ) : (
            <Card className="border border-[hsl(var(--border))] divide-y divide-[hsl(var(--border))]" data-testid="audit-timeline">
              <div className="px-5 py-1">
                {entries.map((e, i) => (
                  <AuditEntry key={`${e.timestamp}-${i}`} entry={e} index={i} />
                ))}
              </div>
            </Card>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4" data-testid="audit-pagination">
              <Button variant="outline" size="sm" className="rounded-full" disabled={page <= 1}
                onClick={() => setPage(p => p - 1)} data-testid="audit-prev">
                <ChevronLeft className="h-4 w-4 mr-1" /> Previous
              </Button>
              <span className="text-sm text-[hsl(var(--muted-foreground))]">
                Page {page} of {totalPages}
              </span>
              <Button variant="outline" size="sm" className="rounded-full" disabled={page >= totalPages}
                onClick={() => setPage(p => p + 1)} data-testid="audit-next">
                Next <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
